// 'use client';

import { Modifier } from '@/components/pages/itemview';
import { ModifierTypes } from '../types/modifiers';
import crypto from 'crypto';
import moment from 'moment-timezone';
import { AmountMoneyObj, OrderDetails } from '@/components/modals/order-details-sheet';
// import React from 'react';
// import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
// import Tooltip from 'react-bootstrap/Tooltip';

export const basePath = process.env.NEXT_PUBLIC_BASE_URL;
export const googleApiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
export const googleAnalyticsId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
export const squareEnvironment = process.env.NEXT_PUBLIC_SQUARE_ENVIRONMENT;
export const squareLocationId = process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID;
export const squareApplicationId = process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID;
export const redisUrl = process.env.NEXT_PUBLIC_REDIS_URL || 'redis://localhost:6379';

// Notes character limit
export const notesCharLimit = 100;

export const deConvertThumb = '6jSO5TXae7upYR2ymjJNcLRdcoq6f8rn';

export interface OpeningHour {
  day: string;
  // startTime: string;
  // endTime: string;
  isOpen: boolean;
  openingHours: Array<{
    startTime: string;
    endTime: string;
  }>;
}

export interface ServiceCharge {
  name: string;
  percentage: number;
}

export interface RangeLimit {
  amount: number;
  currency: string;
  rangeStart: number;
  rangeEnd: number;
  rangeUnit: string;
}

export interface PaymentLimits {
  allowCashOnDelieveryLimit: {amount: number, currency: string};
  allowCashOnPickupLimit: {amount: number, currency: string};
  allowDeliveryRanges: {max: number, freeRadius: number, unit: string};
  deliveryChargePer1km: {amount: number, currency: string};
  orderValueDeliveryLimit: {amount: number, currency: string};
  allowCashOnDelivery: boolean;
  currency: string;
  ranges: {
    ranges: RangeLimit[];
  };
} 

export interface TippingDetails {
  enabled: boolean;
  defaultPercent: number;
  percentages: number[];
  defaultSmartTip: AmountMoneyObj | null;
  smartTippingEnabled: boolean;
  smartTips: Array<AmountMoneyObj>;
}
export interface CompanyDetails{
  locationId: string;
  name: string;
  tagline: string;
  allowCard: boolean;
  allowCash: boolean;
  allowDealItemQty: boolean;
  email: string;
  reviewUrl:string;
  additionalInfo: {
    openingHours: Array<OpeningHour>;
    deliveryRadius: {
      max: number;
      freeRadius: number;
      unit: string;
    };
  };
  address: {
    addressLine1: string;
    administrativeDistrictLevel1: string;
    country: string;
    locality: string;
    postalCode: string;
  };
  language: string;
  description: string;
  phone: string;
  socialMedia: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
  website: string;
  timeZone: string;
  cordinates: {
    latitude: number;
    longitude: number;
  };
  paymentLimits: PaymentLimits;
  locationContactNo: string;
  serviceCharges: Array<ServiceCharge>;
  shopStatus: string;
  serverTime: string;
  tippingDetails: TippingDetails;
  isDeliveryOn: boolean;
  pickupPrepTimeInMinutes: number;
  deliveryPrepTimeInMinutes: number;
  defaultMaxCounts: {
    deliveryMaxCount: number;
    globalMaxCount: number;
    pickupMaxCount: number;
  };
  fulfillmentOffSchedule: Array<{
    startDateTime: string;
    endDateTime: string;
    deliveryMaxCount: number;
    pickupMaxCount: number;
    globalMaxCount: number;
  }>;
}

export type SlotType = "PICKUP" | "DELIVERY";

export interface BaseTimeRange {
    startTime: string; // ISO
    endTime: string;   // ISO
}

export interface TimeRangeConfigItem {
    type: SlotType;
    startDateTime: string; // ISO
    endDateTime: string;   // ISO
}

export interface TimeRangeItem {
  startDateTime: string;
  endDateTime: string;
  deliveryMaxCount: number;
  pickupMaxCount: number;
  globalMaxCount: number;
}

export type TimeRangeConfig = TimeRangeItem[];

export interface OutputSlot {
    type: SlotType;
    startTime: string;
    endTime: string;
}

export interface UnavailableTimeRange {
    startDateTime: string;
    endDateTime: string;
    deliveryMaxCount: number;
    pickupMaxCount: number;
    globalMaxCount: number;
}

interface RearrangedOpeningHour {
  day: string;
  isOpen: boolean;
  openingHours: Array<{ 
    startTime: string; // ISO format: "2026-01-26T03:30:00.000Z"
    endTime: string;   // ISO format: "2026-01-26T15:30:00.000Z"
  }>;
}

interface ConvertedSlot {
  day: string;
  startTime: string; // HH:mm format for comparison
  endTime: string;   // HH:mm format for comparison
  startTimeISO: string; // Full ISO format
  endTimeISO: string;   // Full ISO format
  startDate: Date;
  endDate: Date;
  weekNumber: number; // Track which week this slot belongs to
}

interface ConvertedSlot {
  day: string;
  startTime: string; // HH:mm format for comparison
  endTime: string;   // HH:mm format for comparison
  startTimeISO: string; // Full ISO format
  endTimeISO: string;   // Full ISO format
  startDate: Date;
  endDate: Date;
}

export interface AvailableSlot {
    type: SlotType;
    startTime: string;
    endTime: string;
}

export const availableLanguages = [
  {
      code: "en",
      name: "English"
  }, {
      code: "fr",
      name: "French"
  }
];

export const currencyCode = "USD";

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export function formatCurrency(value: number, currencyCode = 'USD', locale = 'en-US') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  }).format((value / 100));
}

//crypto md5 encryption with environment key
export function encryptData(data: string): string {
  const keyString = deConvertThumb;
  // Ensure the key is 32 bytes for aes-256-cbc
  try {
    const key = Buffer.alloc(32);
    Buffer.from(keyString).copy(key);
    const iv = Buffer.alloc(16, 0); // Use a fixed IV for demonstration; replace with your actual IV handling
    const cipher = crypto.createCipheriv('aes-256-cbc', key, iv);
    let encrypted = cipher.update(data, 'utf8', 'hex');
    encrypted += cipher.final('hex');
    return encrypted;
    
  } catch (error) {
    console.error('Encryption error:', error);
    return '';
  }
}

export function decryptData(encryptedData: string): string {
  const keyString = deConvertThumb;
  // Ensure the key is 32 bytes for aes-256-cbc
  try {
    const key = Buffer.alloc(32);
    Buffer.from(keyString).copy(key);
    const iv = Buffer.alloc(16, 0); // Use a fixed IV for demonstration; replace with your actual IV handling
    const decipher = crypto.createDecipheriv('aes-256-cbc', key, iv);
    let decrypted = decipher.update(encryptedData, 'hex', 'utf8');
    decrypted += decipher.final('utf8');
    return decrypted;

  } catch (error) {
    console.error('Decryption error:', error);
    return '';
  }
}

//thousands separator for numbers
export function formatNumberWithCommas(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

interface FormattedAddress {
  addressLine1: string;
  locality: string;
  administrativeDistrictLevel1: string;
  country: string;
}

export function convertFormattedAddressToAddress(formattedAddress: FormattedAddress | null): string {
  if (!formattedAddress) return '';
  const { addressLine1, locality, administrativeDistrictLevel1, country } = formattedAddress;
  return [
    addressLine1,
    locality,
    administrativeDistrictLevel1,
    country
  ].filter(Boolean).join(', ');
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

// Remove all spaces from a string
export function removeSpacesFromString(input: string | undefined): string {
  if (!input) return '';
  return input.replace(/\s+/g, '').toLowerCase();
}

export function timezoneDateConvert({ utcDate, timezone, format }: { utcDate: string; timezone?: string; format?: string }) {
  // Convert UTC date to the desired timezone
  // console.log(utcDate, timezone);

  const serverMoment = moment.tz(utcDate, (timezone || moment.tz.guess()));

  const browserTimezone = moment.tz.guess();
  const localMoment  = serverMoment.tz(browserTimezone);

  const localTime = localMoment.format(format || 'YYYY-MM-DDTHH:mm:ss');

  return localTime;
};

export function modifiersFilter (modifiers: Modifier[], modifierTypes: Array<ModifierTypes[keyof ModifierTypes]>) {
  const sendModifieres = [] as Array<Modifier>;

  modifierTypes.forEach((modifierType) => {
    if(modifierType === "base") {
      // Apply base filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("base") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "dietary") {
      // Apply dietary filters
      modifiers?.forEach((modifier) => {
        if((modifier.modifierName.toLowerCase().includes("dietary") || modifier.modifierName.toLowerCase().includes("gluten")) && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });

    } else if(modifierType === "add") {
      // Apply add filters
      modifiers?.forEach((modifier) => {
        if(
          (!modifier.modifierName.toLowerCase().includes("base") && !modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten")) &&
          ((modifier.modifierName.toLowerCase().includes("add") || modifier.modifierName.toLowerCase().includes("extra") || modifier.modifierName.toLowerCase().includes("topping"))
        ) && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });

    } else if(modifierType === "removal") {
      // Apply removal filters
      modifiers?.forEach((modifier) => {
        if(
          (!modifier.modifierName.toLowerCase().includes("base") && !modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten")) &&
          modifier.modifierName.toLowerCase().includes("remov") && modifier.mofireOptions.length > 0
        ) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "ingredient") {
      // Apply ingredient filters
      modifiers?.forEach((modifier) => {
        if(
          (!modifier.modifierName.toLowerCase().includes("base") && !modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten")) &&
          modifier.modifierName.toLowerCase().includes("ingred") && modifier.mofireOptions.length > 0
        ) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "notdietary") {
      // Apply not dietary filters
      modifiers?.forEach((modifier) => {
        if(!modifier.modifierName.toLowerCase().includes("dietary") && !modifier.modifierName.toLowerCase().includes("gluten") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half1") {
      // Apply halfside1 filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("half 1") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half2") {
      // Apply halfside2 filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("half 2") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half1customs") {
      // Apply halfside1customs filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("pizza 1") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    } else if(modifierType === "half2customs") {
      // Apply halfside2customs filters
      modifiers?.forEach((modifier) => {
        if(modifier.modifierName.toLowerCase().includes("pizza 2") && modifier.mofireOptions.length > 0) {
          sendModifieres.push(modifier);
        }
      });
    }
  });

  return sendModifieres;
}

export function checkRefundsAvailable(order: OrderDetails): {
  isAvailable: boolean
  refundState: string
} {
  if(order?.payments && order?.payments.length > 0) {
    const refundState = order.payments.find(payment => payment.refunds && payment.refunds.length > 0)?.refunds?.[0]?.refundStatus || 'NONE';
    return {
      isAvailable: order.payments.some(payment => payment.refunds && payment.refunds.length > 0),
      refundState: (refundState === 'COMPLETED' ? 'REFUNDED' : refundState === "PENDING" ? 'REFUND_PENDING' : refundState === "REJECTED" ? 'REFUND_REJECTED' : refundState === "FAILED" ? 'REFUND_FAILED' : 'NONE')
    };
  }
  return {
    isAvailable: false,
    refundState: 'NONE'
  };
}

// Capitalize the first letter of each word in a string
export function capitalizeText(text: string): string {
  return text.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}

function subtractUnavailableRanges(
    baseStart: Date,
    baseEnd: Date,
    scheduleTimeGap: number,
    unavailable: UnavailableTimeRange[]
): Array<{ start: Date; end: Date }> {
    let slots = [{ start: baseStart, end: baseEnd }];

      unavailable.forEach(({ startDateTime, endDateTime, globalMaxCount}) => {
        const uStart = new Date(startDateTime);
        const uEnd = new Date(endDateTime);

        slots = slots.flatMap(slot => {
            // No overlap
            if (uEnd <= slot.start || uStart >= slot.end) {
                return [slot];
            }

            const newSlots = [];

            // // Left side
            // if (uStart > slot.start) {
            //     // newSlots.push({ start: slot.start, end: uStart , globalMaxCountVal: -1});
            //     newSlots.push({ start: slot.start, end: uStart });
            // }

            // // Right side
            // if (uEnd < slot.end) {
            //     newSlots.push({ start: uEnd, end: slot.end });
            //     // newSlots.push({ start: uEnd, end: slot.end ,globalMaxCountVal: globalMaxCount});
            // }

            // Left side (before unavailable period)
            if (uStart > slot.start) {
                const leftEnd = new Date(uStart);
                leftEnd.setMinutes(leftEnd.getMinutes() - 15); // Subtract 15 minute
                newSlots.push({ start: slot.start, end: leftEnd });
            }

            // Right side (after unavailable period)
            if (uEnd < slot.end) {
                const rightStart = new Date(uEnd);
                rightStart.setMinutes(globalMaxCount === 0 ? rightStart.getMinutes() + scheduleTimeGap : rightStart.getMinutes());
                newSlots.push({ start: rightStart, end: slot.end });
            }

            return newSlots;
        });
    });

    return slots;
}



// Break down base time ranges into specific slots based on type
export function getAvailableTimeSlotsByType(
    selectedServiceDate: Date,
    baseRanges: BaseTimeRange[],
    timeRangeConfig: TimeRangeConfig,
    type: SlotType,
    scheduleTimeGap: number,
): AvailableSlot[] {
    // const unavailableRanges =
    //     type === "PICKUP"
    //         ? timeRangeConfig.pickupTimeRanges ?? []
    //         : timeRangeConfig.deliveryTimeRanges ?? [];

    // Ensure timeRangeConfig is an array before filtering
    timeRangeConfig = Array.isArray(timeRangeConfig) ? timeRangeConfig : [];

    if( type === "PICKUP"){
      timeRangeConfig = timeRangeConfig.filter(range => 
        (range.pickupMaxCount === 0 && range.globalMaxCount > 0 && range.deliveryMaxCount > 0) || 
        (range.globalMaxCount === 0 && range.pickupMaxCount === 0 && range.deliveryMaxCount === 0)
      );
    }
    if( type === "DELIVERY"){
      timeRangeConfig = timeRangeConfig.filter(range => 
        (range.deliveryMaxCount === 0 && range.globalMaxCount > 0 && range.pickupMaxCount > 0) || 
        (range.globalMaxCount === 0 && range.pickupMaxCount === 0 && range.deliveryMaxCount === 0)
      );
    }

    const unavailableRanges = timeRangeConfig ;

    const result: AvailableSlot[] = [];

    baseRanges.forEach(({ startTime, endTime }) => {
        const baseStart = new Date(startTime);
        baseStart.setFullYear(
            selectedServiceDate.getFullYear(),
            selectedServiceDate.getMonth(),
            selectedServiceDate.getDate()
        );
        const baseStartDayOfYear = moment(baseStart).format('ddd').toUpperCase();

        const baseEnd = new Date(endTime);
        baseEnd.setFullYear(
            selectedServiceDate.getFullYear(),
            selectedServiceDate.getMonth(),
            selectedServiceDate.getDate()
        );

        const availableSlots = subtractUnavailableRanges(
            baseStart,
            baseEnd,
            scheduleTimeGap,
            unavailableRanges.filter(range => moment(range.startDateTime).format('ddd').toUpperCase() === baseStartDayOfYear)
        );

        availableSlots.forEach(slot => {
            if (slot.start < slot.end ||  slot.end.getHours() === 0 && slot.end.getMinutes() === 0) {
                result.push({
                    type,
                    startTime: slot.start.toISOString(),
                    endTime: slot.end.toISOString()
                });
            }
        });
    });

    return result;
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function mergeTimeSlots(slots: Array<{ startTime: string; endTime: string }>): Array<{ startTime: string; endTime: string }> {
  if (slots.length === 0) return [];

  // Sort by start time
  const sorted = [...slots].sort((a, b) => {
    return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  });

  const merged: Array<{ startTime: string; endTime: string }> = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    const currentEnd = new Date(current.endTime);
    const nextStart = new Date(next.startTime);
    const nextEnd = new Date(next.endTime);

    // Check if this is a midnight boundary (00:00:00)
    const isCurrentEndMidnight = 
      currentEnd.getUTCHours() === 0 && 
      currentEnd.getUTCMinutes() === 0 && 
      currentEnd.getUTCSeconds() === 0;

    const isNextStartMidnight = 
      nextStart.getUTCHours() === 0 && 
      nextStart.getUTCMinutes() === 0 && 
      nextStart.getUTCSeconds() === 0;

    // If both are at midnight and same timestamp, don't merge (day boundary)
    const isMidnightBoundary = 
      isCurrentEndMidnight && 
      isNextStartMidnight && 
      currentEnd.getTime() === nextStart.getTime();

    // Check if slots overlap or are adjacent (within 1 minute)
    // BUT don't merge if it's a midnight boundary
    if (!isMidnightBoundary && (nextStart <= currentEnd || (nextStart.getTime() - currentEnd.getTime()) <= 60000)) {
      // Merge: extend current end time if next ends later
      if (nextEnd > currentEnd) {
        current.endTime = next.endTime;
      }
    } else {
      // No overlap or it's a midnight boundary: push current and start new slot
      merged.push(current);
      current = { ...next };
    }
  }

  // Push the last slot
  merged.push(current);

  return merged;
}

export function rearrangeOpeningHours(
  openingHours: OpeningHour[],
  timezone: string
): RearrangedOpeningHour[] {
  const dayMap: { [key: string]: { index: number; name: string } } = {
    'MON': { index: 0, name: 'Monday' },
    'TUE': { index: 1, name: 'Tuesday' },
    'WED': { index: 2, name: 'Wednesday' },
    'THU': { index: 3, name: 'Thursday' },
    'FRI': { index: 4, name: 'Friday' },
    'SAT': { index: 5, name: 'Saturday' },
    'SUN': { index: 6, name: 'Sunday' },
  };

  const reverseDayMap: { [key: number]: string } = {
    0: 'MON',
    1: 'TUE',
    2: 'WED',
    3: 'THU',
    4: 'FRI',
    5: 'SAT',
    6: 'SUN',
  };

  // Collect all converted time slots with day information
  const allSlots: ConvertedSlot[] = [];
  let baseWeekNumber: number | null = null;
  let allowedWeeks: number[] = []; // Track allowed weeks

  openingHours.forEach((dayHours) => {

    (dayHours.openingHours || []).forEach((slot) => {
      if (!slot.startTime || !slot.endTime) return;

      // Convert to timezone and get full date objects
      const startConverted = timezoneDateConvert({
        utcDate: slot.startTime,
        timezone: timezone,
        format: 'YYYY-MM-DD HH:mm'
      });

      const endConverted = timezoneDateConvert({
        utcDate: slot.endTime,
        timezone: timezone,
        format: 'YYYY-MM-DD HH:mm'
      });

      const startDate = new Date(startConverted);
      const endDate = new Date(endConverted);

      // Set base week number from first slot and allow adjacent week
      if (baseWeekNumber === null) {
        baseWeekNumber = getWeekNumber(startDate);
        allowedWeeks = [baseWeekNumber, baseWeekNumber + 1]; // Allow current and next week
      }

      // Get the day of week for start time
      const startDayIndex = startDate.getDay();
      const adjustedStartDay = startDayIndex === 0 ? 6 : startDayIndex - 1; // Convert Sunday(0) to 6

      // Extract time portions for comparison
      const startTime = timezoneDateConvert({
        utcDate: slot.startTime,
        timezone: timezone,
        format: 'HH:mm'
      });

      const endTime = timezoneDateConvert({
        utcDate: slot.endTime,
        timezone: timezone,
        format: 'HH:mm'
      });

      // Check if the slot crosses midnight
      if (endDate.getDate() !== startDate.getDate() || endDate < startDate) {
        // Split into two slots

        const startWeek = getWeekNumber(startDate);
        const endWeek = getWeekNumber(endDate);

        // First slot ends at 23:59:59.999 of the start day (end of MON)
        const endOfStartDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 23, 59, 59, 999);

        // Second slot starts at 00:00:00.000 of the end day (start of TUE)
        const startOfEndDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0, 0);

        // First slot: start time to end of day (23:59:59.999)
        // Only add if it's in the allowed weeks
        if (allowedWeeks.includes(startWeek)) {
          allSlots.push({
            day: reverseDayMap[adjustedStartDay],
            startTime: startTime,
            endTime: '23:59',
            startTimeISO: startDate.toISOString(),
            endTimeISO: endOfStartDay.toISOString(), // 23:59:59.999 of the start day
            startDate: startDate,
            endDate: endOfStartDay,
            weekNumber: startWeek
          });
        }

        // Second slot: start of day (00:00:00.000) to end time
        // Only add if it's in the allowed weeks
        if (allowedWeeks.includes(endWeek)) {
          const endDayIndex = endDate.getDay();
          const adjustedEndDay = endDayIndex === 0 ? 6 : endDayIndex - 1;

          allSlots.push({
            day: reverseDayMap[adjustedEndDay],
            startTime: '00:00',
            endTime: endTime,
            startTimeISO: startOfEndDay.toISOString(), // 00:00:00.000 of the end day
            endTimeISO: endDate.toISOString(),
            startDate: startOfEndDay,
            endDate: endDate,
            weekNumber: endWeek
          });
        }
      } else {
        // Normal slot within same day
        const slotWeek = getWeekNumber(startDate);

        // Only add if it's in the allowed weeks
        if (allowedWeeks.includes(slotWeek)) {
          allSlots.push({
            day: reverseDayMap[adjustedStartDay],
            startTime: startTime,
            endTime: endTime,
            startTimeISO: startDate.toISOString(),
            endTimeISO: endDate.toISOString(),
            startDate: startDate,
            endDate: endDate,
            weekNumber: slotWeek
          });
        }
      }
    });
  });

  // Group slots by day
  // IMPORTANT: We use slot.day (the assigned day) not the timestamp's day
  // This keeps MON ending at midnight separate from TUE starting at midnight
  const slotsByDay: { [key: string]: Array<{ startTime: string; endTime: string }> } = {};

  allSlots.forEach((slot) => {
    if (!slotsByDay[slot.day]) {
      slotsByDay[slot.day] = [];
    }
    slotsByDay[slot.day].push({
      startTime: slot.startTimeISO,
      endTime: slot.endTimeISO
    });
  });

  // Create final result with all days and merge overlapping slots
  const result: RearrangedOpeningHour[] = Object.keys(dayMap).map((dayKey) => {
    const slots = slotsByDay[dayKey] || [];

    // Merge overlapping/consecutive slots
    const mergedSlots = mergeTimeSlots(slots);

    return {
      day: dayKey,
      isOpen: mergedSlots.length > 0,
      openingHours: mergedSlots
    };
  });

  return result;
}